import * as Sentry from "@sentry/browser";
import type { Breadcrumb, ErrorEvent, User as SentryUser } from "@sentry/browser";
import { getSessionUser } from "./users/auth";
import type { User } from "./users/model";
import Configuration from "../config";
import { LocalStorageKeys } from "utils/storageKeys";
import { ApiRequestError, QueryError, toApiRequestError } from "services/errors";
import { SECRET_PARAMS } from "services/payments/urlSecrets";

export const LOG_LEVEL = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
  DEBUG: "debug",
} as const;

type LogLevel = (typeof LOG_LEVEL)[keyof typeof LOG_LEVEL];

type NetworkInfo = {
  downlink?: number;
  downlinkMax?: number;
  effectiveType?: string;
  rtt?: number;
  type?: string;
};

// The person is named by id; their name and email stay out of the user context.
type UserLoggingContext = SentryUser & {
  id: string;
  utc_offset: string;
  network_info: NetworkInfo;
};

const LOGGER_URL = "https://bc6c71e6c0e64fe19da19315c10aa826@sentry.io/283084";
const DEFAULT_LEVEL: LogLevel = LOG_LEVEL.INFO;
const LESSON_TAG_NAME = "lesson.id";
// A tab repeating one message (a failing poll, a retry loop) says nothing new
// after the first few, and each repeat still spends quota.
const MAX_SENDS_PER_MESSAGE = 10;
// A breadcrumb rides on every later event, so it carries a bounded copy of its
// data: a whole SDK event object would add kilobytes to each.
const BREADCRUMB_DATA_DEPTH = 2;
const BREADCRUMB_DATA_ENTRIES = 20;
const BREADCRUMB_STRING_LENGTH = 200;
let consoleLoggingEnabled = false;
let loggerConfigured = false;
const sendCounts = new Map<string, number>();

export function configLogger() {
  consoleLoggingEnabled = Boolean(
    Configuration.ENABLE_CONSOLE_LOGGING ||
      localStorage.getItem(LocalStorageKeys.enableConsoleLogging)
  );

  if (!Configuration.ENABLE_REMOTE_LOGGING) {
    return;
  }

  Sentry.init({
    dsn: LOGGER_URL,
    release: __BUILD_VERSION__,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
  });
  loggerConfigured = true;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb) {
  const data = breadcrumb.data;
  if (data) {
    for (const key of ["from", "to", "url"]) {
      const value: unknown = data[key];
      // Assigned only on a change, so a read-only value passes through.
      if (typeof value === "string" && scrubUrl(value) !== value) {
        data[key] = scrubUrl(value);
      }
    }
  }
  return breadcrumb;
}

function scrubEvent(event: ErrorEvent) {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  return event;
}

// OAuth's code and state are not in SECRET_PARAMS: the callback page reads them
// from the address bar.
const SECRET_QUERY_PARAMS = new RegExp(
  `([?&](?:${[...SECRET_PARAMS, "code", "state"].join("|")})=)[^&#]*`,
  "g"
);

function scrubUrl(url: string) {
  return url.replace(SECRET_QUERY_PARAMS, "$1[Filtered]");
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInfo;
};

function getNetworkInfo(): NetworkInfo {
  const networkInfo = (navigator as NavigatorWithConnection).connection;
  if (!networkInfo) {
    return {};
  }

  return {
    downlink: networkInfo.downlink,
    downlinkMax: networkInfo.downlinkMax,
    effectiveType: networkInfo.effectiveType,
    rtt: networkInfo.rtt,
    type: networkInfo.type,
  };
}

function buildUserLoggingContext(user: User): UserLoggingContext {
  const context: UserLoggingContext = {
    id: user.id,
    utc_offset: user.profile.timezone.utc_offset,
    network_info: getNetworkInfo(),
  };

  return context;
}

export function updateUserLoggingContext(user: User) {
  if (!loggerConfigured) {
    return;
  }

  Sentry.setUser(buildUserLoggingContext(user));
}

export function clearUserContext() {
  if (!loggerConfigured) {
    return;
  }

  Sentry.setUser(null);
}

function logToConsole(method: "log" | "warn" | "error" | "debug", ...args: unknown[]) {
  if (!consoleLoggingEnabled) {
    return;
  }

  // eslint-disable-next-line no-console
  console[method](...args);
}

export function log(message: unknown, data?: unknown, level: LogLevel = DEFAULT_LEVEL) {
  if (!loggerConfigured) {
    return;
  }

  // Logging runs inside click handlers and catch blocks: a failure here must not
  // stop the code that called it.
  try {
    send(message, data, level);
  } catch (error) {
    logToConsole("error", "Could not send a log to Sentry", error);
  }
}

function send(message: unknown, data: unknown, level: LogLevel) {
  const user = getSessionUser();
  if (user) {
    updateUserLoggingContext(user);
  }

  const apiError = toApiRequestError(message) ?? toApiRequestError(data);
  const text = scrubUrl(stringifyMessage(message));

  // Info and debug narrate what led up to a failure: as breadcrumbs they ride
  // along on the next event instead of each spending quota as one.
  if (level === LOG_LEVEL.INFO || level === LOG_LEVEL.DEBUG) {
    Sentry.addBreadcrumb({
      category: "log",
      level,
      message: text,
      data: toBreadcrumbData(data),
    });
    return;
  }

  const error = findCodeError(message, data);
  // Counted by the error, not by the line logging it: every render crash shares
  // the error boundary's line, and every API failure of one status shares a title.
  const sendKey = error
    ? `${level}:${error.name}:${error.message}`
    : `${level}:${text}:${apiError?.response?.url ?? ""}`;
  if (!claimSend(sendKey)) {
    return;
  }

  // An error passed as data travels as the exception, or as the API extras.
  const extra = data instanceof Error ? {} : toExtras(data);
  if (error) {
    Sentry.captureException(error, { level, extra: { ...extra, log_message: text } });
    return;
  }

  Sentry.captureMessage(text, {
    level,
    extra: { ...apiErrorExtras(apiError), ...extra },
  });
}

function stringifyMessage(message: unknown) {
  const apiError = toApiRequestError(message);
  // An API error is built without a message; its status is what tells one
  // failure from another.
  if (apiError) {
    return `API request failed (${apiError.response?.status ?? "no response"})`;
  }

  return String(message);
}

function toBreadcrumbData(data: unknown) {
  return boundedCopy(toExtras(data), BREADCRUMB_DATA_DEPTH) as Record<string, unknown>;
}

function boundedCopy(value: unknown, depth: number): unknown {
  if (value instanceof Error) {
    return String(value).slice(0, BREADCRUMB_STRING_LENGTH);
  }
  if (typeof value === "string") {
    return value.slice(0, BREADCRUMB_STRING_LENGTH);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (depth === 0) {
    return Array.isArray(value) ? "[Array]" : "[Object]";
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, BREADCRUMB_DATA_ENTRIES)
      .map((item: unknown) => boundedCopy(item, depth - 1));
  }

  return Object.fromEntries(
    Object.entries(value)
      .slice(0, BREADCRUMB_DATA_ENTRIES)
      .map(([key, item]) => [key, boundedCopy(item, depth - 1)])
  );
}

function claimSend(key: string) {
  const sends = (sendCounts.get(key) ?? 0) + 1;
  sendCounts.set(key, sends);
  return sends <= MAX_SENDS_PER_MESSAGE;
}

// The caught error a call passes as its message (`.catch(logError)`) or beside
// it (`logError("…", e)`, `{ err: e }`), sent as an exception to keep its stack.
// A query's wrapper gives way to what its fetcher threw, and an API error is left
// out: its stack is restUtil's whichever request failed.
function findCodeError(message: unknown, data: unknown) {
  const nested =
    data !== null && typeof data === "object"
      ? (data as { err?: unknown; error?: unknown })
      : {};

  return [message, data, nested.err, nested.error]
    .map(value => (value instanceof QueryError ? value.fetchError : value))
    .find(
      (value): value is Error =>
        value instanceof Error && !(value instanceof ApiRequestError)
    );
}

function apiErrorExtras(error: ApiRequestError | undefined) {
  if (!error) {
    return {};
  }

  const url = error.response?.url;
  return {
    status: error.response?.status,
    url: typeof url === "string" ? scrubUrl(url) : undefined,
    response_body: error.responseBody,
  };
}

// Sentry types extras as a keyed record; raven accepted any value, so keep
// object payloads intact and wrap primitives to preserve them in the event.
function toExtras(data: unknown): Record<string, unknown> {
  if (data === null || data === undefined) {
    return {};
  }

  if (typeof data === "object") {
    return data as Record<string, unknown>;
  }

  return { data };
}

export function logInfo(message: unknown, data?: unknown) {
  log(message, data, LOG_LEVEL.INFO);
  logToConsole("log", message, data);
}

export function logError(message: unknown, data?: unknown) {
  log(message, data, LOG_LEVEL.ERROR);
  logToConsole("error", message, data);
}

export function logWarning(message: unknown, data?: unknown) {
  log(message, data, LOG_LEVEL.WARNING);
  logToConsole("warn", message, data);
}

/**
 * The raw user agent, for logs and nothing else. It is unreliable — a client can say
 * whatever it likes.
 */
export function getUserAgent() {
  return navigator.userAgent;
}

export function logDebug(...args: unknown[]) {
  logToConsole("debug", ...args);
}

export function setLessonTag(id: string | number) {
  if (!loggerConfigured) {
    return;
  }

  Sentry.setTag(LESSON_TAG_NAME, String(id));
}

export function removeLessonTag() {
  if (!loggerConfigured) {
    return;
  }

  // Undefined-valued tags are dropped during event serialization, which is
  // Sentry's documented way to remove a tag from the scope.
  Sentry.setTag(LESSON_TAG_NAME, undefined);
}
